import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom"; // 🔹 Importa useNavigate
import { useParams } from 'react-router-dom';
import { Tree, Input, Checkbox, Button, Form, message, Drawer, Popconfirm } from 'antd';
import { RedoOutlined } from '@ant-design/icons';


import {
    getPoasSostenibilidad,
    updatePoasSostenibilidad
} from "../../services/sostenibilidad/poaSosteibilidad";

function DynamicTree() {
    const navigate = useNavigate(); // 🔹 Hook para navegar
    const { codigoInterno } = useParams();


    const [showJson, setShowJson] = useState(false);
    const [arbolInicial, setArbolInicial] = useState([]);

    // 🔍 Obtener un POA por código de proyecto (Ejecutar solo cuando cambia el código interno)
    useEffect(() => {
        const fetchPoaByCodigo = async () => {
            if (!codigoInterno) {
                console.warn("⚠️ No se proporcionó un código interno.");
                return;
            }
            try {
                const data = await getPoasSostenibilidad(codigoInterno);

                if (data && data.poas) {

                    setArbolInicial(data.poas);
                    setTreeData(data.poas);
                } else {
                    console.warn("⚠️ No se encontraron datos en `data.poas`.");
                }
            } catch (error) {
                console.error("❌ Error al buscar el POA:", error);
            }
        };

        fetchPoaByCodigo();
    }, [codigoInterno]);

    const [treeData, setTreeData] = useState(arbolInicial);
    // Form de AntD
    const [form] = Form.useForm();

    // Guarda la key del nodo en edición (o null si es uno nuevo)
    const [editingKey, setEditingKey] = useState(null);

    // Guarda información del nodo seleccionado (o null si es uno nuevo)
    const [selectedNode, setSelectedNode] = useState(null);
    const [isFinal, setIsFinal] = useState(false);

    // Controla la visibilidad del Drawer
    const [drawerVisible, setDrawerVisible] = useState(false);


    // Función para regresar a la página anterior
    const handleBack = () => {
        navigate(-1); // 🔙 Regresa a la página anterior en la historia
    };


    /**
     * Busca un nodo por su 'key' y retorna { node, parentKey }
     */
    function findNodeByKey(data, key, parentKey = null) {
        for (let i = 0; i < data.length; i++) {
            const item = data[i];
            if (item.key === key) {
                return { node: item, parentKey };
            }
            if (item.children) {
                const result = findNodeByKey(item.children, key, item.key);
                if (result) {
                    return result;
                }
            }
        }
        return null;
    }

    /**
     * Actualiza un nodo por key, creando un árbol nuevo (inmutable)
     */
    function updateNodeByKey(data, key, newFields) {
        return data.map((item) => {
            if (item.key === key) {
                // Retornamos un nuevo objeto con las props actualizadas
                return {
                    ...item,
                    ...newFields,
                };
            } else if (item.children) {
                return {
                    ...item,
                    children: updateNodeByKey(item.children, key, newFields),
                };
            } else {
                return item;
            }
        });
    }

    /**
     * onSelect: cuando se hace clic en un nodo, abrimos el Drawer en modo edición.
     */
    const onSelect = (selectedKeys) => {
        if (selectedKeys.length === 0) {
            setEditingKey(null);
            setSelectedNode(null);
            setDrawerVisible(false);
            form.resetFields();
            return;
        }

        const selectedKey = selectedKeys[0];
        const result = findNodeByKey(treeData, selectedKey);

        if (!result || !result.node) {
            message.error('No se encontró el nodo seleccionado');
            return;
        }

        setEditingKey(selectedKey);
        setSelectedNode(result.node); // 🔥 Guarda el nodo seleccionado para futuros usos
        setDrawerVisible(true);

        // Convertimos "yes"/"no" a booleano
        const isFinalValue = result.node.isFinal === 'yes';
        setIsFinal(isFinalValue); // 🔥 Ahora se sincroniza correctamente

        // Cargar datos en el formulario
        form.setFieldsValue({
            nodeKey: selectedKey,
            parentKey: result.parentKey || '',
            title: result.node.title,
            tipoActividad: result.node.tipoActividad || '',
            actor: result.node.actor || '',
            mdv: result.node.mdv || '',
            noDeActividades: result.node.noDeActividades || 0,
            noDeEjecutados: result.node.noDeEjecutados || 0,
            isFinal: isFinalValue // 🔥 Se asegura de que el checkbox se marque correctamente
        });
    };

    /**
     * handleSubmit: crea o edita un nodo
     */
    const handleSubmit = (values) => {
        const {
            title,
            parentKey,
            tipoActividad,
            actor,
            mdv,
            noDeActividades,
            noDeEjecutados,
            isFinal // boolean (true/false) según el Checkbox
        } = values;

        if (!title) {
            message.error('El título es obligatorio');
            return;
        }

        // Copiamos el treeData para no mutarlo
        let clonedData = JSON.parse(JSON.stringify(treeData));

        if (editingKey === null) {
            // MODO CREAR
            const newKey = Date.now().toString();

            // Base del nodo
            const newNode = {
                title,
                key: newKey,
                children: [],
                // Convertimos el boolean en "yes"/"no"
                isFinal: isFinal ? 'yes' : 'no'
            };

            // Si es final => guardamos campos extra
            if (isFinal) {
                newNode.tipoActividad = tipoActividad || '';
                newNode.actor = actor || '';
                newNode.mdv = mdv || '';
                newNode.noDeActividades = parseInt(noDeActividades, 10) || 0;
                newNode.noDeEjecutados = parseInt(noDeEjecutados, 10) || 0;
            }

            // Insertar en el árbol
            if (parentKey) {
                const found = findNodeByKey(clonedData, parentKey);
                if (found && found.node) {
                    found.node.children = found.node.children
                        ? [...found.node.children, newNode]
                        : [newNode];
                } else {
                    message.warning(`No se encontró el nodo con key=${parentKey}. Se agrega a la raíz.`);
                    clonedData.push(newNode);
                }
            } else {
                clonedData.push(newNode);
            }

            setTreeData(clonedData);
            message.success(`Se ha agregado el nodo "${title}" al árbol.`);

        } else {
            // MODO EDITAR
            const found = findNodeByKey(clonedData, editingKey);
            if (!found || !found.node) {
                message.error('No se encontró el nodo a editar');
                return;
            }
            const currentNode = found.node;

            // Estructura base
            const updatedNodeFields = {
                title,
                // Convertimos bool en "yes"/"no"
                isFinal: isFinal ? 'yes' : 'no'
            };

            if (isFinal) {
                updatedNodeFields.tipoActividad = tipoActividad || '';
                updatedNodeFields.actor = actor || '';
                updatedNodeFields.mdv = mdv || '';
                updatedNodeFields.noDeActividades = parseInt(noDeActividades, 10) || 0;
                updatedNodeFields.noDeEjecutados = parseInt(noDeEjecutados, 10) || 0;
            } else {
                // Si no es final, eliminamos campos extra
                delete currentNode.tipoActividad;
                delete currentNode.actor;
                delete currentNode.mdv;
                delete currentNode.noDeActividades;
                delete currentNode.noDeEjecutados;
            }

            // Actualizamos inmutablemente
            clonedData = updateNodeByKey(clonedData, editingKey, updatedNodeFields);
            setTreeData(clonedData);
            message.success(`Se ha actualizado el nodo "${title}".`);
        }

        form.resetFields();
        setEditingKey(null);
        setSelectedNode(null);
        setDrawerVisible(false);
    };


    const handleCloseDrawer = () => {
        setDrawerVisible(false);
        setEditingKey(null);
        setSelectedNode(null);
        form.resetFields();
        setIsFinal(false); // Reinicia el estado del checkbox al cerrar
    };


    /**
     * Abre el Drawer en modo "crear nodo"
     */
    const handleOpenDrawerForNewNode = () => {
        setEditingKey(null);
        setSelectedNode(null);

        form.resetFields();
        form.setFieldsValue({
            nodeKey: '',
            parentKey: selectedNode ? selectedNode.key : '', // 🔥 Se asigna el key del nodo padre si hay uno seleccionado
            title: '',
            tipoActividad: '',
            actor: '',
            mdv: '',
            noDeActividades: 0,
            noDeEjecutados: 0,
            // Por defecto, marcamos 'no' final (false)
            isFinal: false
        });

        setDrawerVisible(true);
    };

    /**
     * onDragEnter, onDrop => arrastrar y soltar
     */
    const onDragEnter = (info) => {
        console.log('onDragEnter', info);
    };

    const onDrop = (info) => {

        const dropKey = info.node.key;
        const dragKey = info.dragNode.key;

        const dropPos = info.node.pos.split('-');
        const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1]);

        const data = [...treeData];
        let dragObj;

        const loop = (items, key, callback) => {
            for (let i = 0; i < items.length; i++) {
                if (items[i].key === key) {
                    callback(items[i], i, items);
                    return;
                }
                if (items[i].children) {
                    loop(items[i].children, key, callback);
                }
            }
        };

        loop(data, dragKey, (item, index, arr) => {
            arr.splice(index, 1);
            dragObj = item;
        });

        if (!info.dropToGap) {
            loop(data, dropKey, (item) => {
                item.children = item.children || [];
                item.children.unshift(dragObj);
            });
        } else {
            let arr = [];
            let insertIndex = 0;
            loop(data, dropKey, (item, index, parentArr) => {
                arr = parentArr;
                insertIndex = index;
            });
            if (dropPosition === -1) {
                arr.splice(insertIndex, 0, dragObj);
            } else {
                arr.splice(insertIndex + 1, 0, dragObj);
            }
        }
        setTreeData(data);
    };

    // Muestra/oculta el JSON
    const toggleShowJson = () => {
        setShowJson(!showJson);
    };

    // 🔄 Sincroniza el estado cuando el usuario cambia el checkbox
    const handleCheckboxChange = (e) => {
        const checked = e.target.checked;
        setIsFinal(checked);
        form.setFieldsValue({ isFinal: checked }); // 🔥 Asegura que el formulario refleje el cambio
    };


    // ✅ Función para actualizar el POA
    const handleUpdatePoa = async () => {
        try {
            const poaActualizado = { poas: treeData }; // ✅ El objeto que enviamos
            await updatePoasSostenibilidad(codigoInterno, poaActualizado);

            message.success("POA actualizado correctamente.");
        } catch (error) {

            message.error("Error al actualizar el POA.");
        }
    };

    const handleDeleteNode = () => {
        if (!editingKey) {
            message.warning('No hay ningún nodo seleccionado para eliminar.');
            return;
        }

        // Función recursiva para eliminar un nodo por key
        const deleteNodeByKey = (data, key) => {
            return data.filter(node => {
                if (node.key === key) {
                    return false; // Se elimina el nodo
                }
                if (node.children) {
                    node.children = deleteNodeByKey(node.children, key); // Recursivo para los hijos
                }
                return true;
            });
        };

        // Clonamos el árbol para no mutar el estado original
        const updatedTree = deleteNodeByKey(treeData, editingKey);

        // Actualizamos el estado
        setTreeData(updatedTree);

        // Cerramos el drawer después de la eliminación
        setEditingKey(null);
        setSelectedNode(null);
        setDrawerVisible(false);

        message.success('Nodo eliminado correctamente.');
    };


    return (

        <div className="container">

            <h2> Sostenibilidad: POA {codigoInterno}</h2>

            <div className="d-flex justify-content-between align-items-center mb-3">

                <div className="d-flex justify-content-end align-items-center gap-3">

                    {/* ➕ Botón de agregar nodo a la derecha */}
                    <Button type="primary" color="cyan" variant="primary" onClick={handleOpenDrawerForNewNode}>
                        Agregar Nodo
                    </Button>
                    {/* ✅ Botón de actualizar con confirmación */}
                    <Popconfirm
                        title={`¿Realmente desea actualizar el POA del proyecto ${codigoInterno}?`}
                        onConfirm={handleUpdatePoa}
                        okText="Sí, actualizar"
                        cancelText="Cancelar"
                    >
                        <Button
                            type="primary"
                            icon={<RedoOutlined />}
                            style={{ backgroundColor: '#198754', borderColor: '#198754', color: 'white' }}
                        >
                            Guardar POA
                        </Button>

                    </Popconfirm>
                </div>


                {/* 🔙 Botón de volver con Popconfirm */}
                <Popconfirm
                    title={
                        <>
                            ¿Si realizó modificaciones en el POA <strong>{codigoInterno}</strong>? <br />
                            Recuerde guardar antes de salir.
                        </>
                    }
                    onConfirm={handleBack} // La acción de volver se ejecuta al confirmar
                    okText="Sí, volver"
                    cancelText="Cancelar"
                >
                    <Button type="default">← Volver</Button>
                </Popconfirm>
            </div>


            {/* Árbol de Ant Design */}
            <Tree
                treeData={treeData}
                draggable
                blockNode
                onSelect={onSelect}
                onDragEnter={onDragEnter}
                onDrop={onDrop}
                style={{ border: '1px solid #eee', padding: '8px' }}
                titleRender={(nodeData) => {
                    // Revisamos nodeData.isFinal
                    if (nodeData.isFinal === 'yes') {
                        // Si es final, mostramos datos extra
                        return (
                            <div>
                                <strong>{nodeData.title}</strong>
                                <br />
                                <span>Tipo: {nodeData.tipoActividad}</span>
                                <br />
                                <span>Actor: {nodeData.actor}</span>
                                <br />
                                <span>MDV: {nodeData.mdv}</span>
                                <br />
                                <span>Actividades: {nodeData.noDeActividades}</span>
                                <br />
                                <span>Ejecutados: {nodeData.noDeEjecutados}</span>
                            </div>
                        );
                    } else {
                        // No final => solo mostramos el title
                        return <span>{nodeData.title}</span>;
                    }
                }}
            />

            {/* Drawer para agregar/editar */}
            <Drawer
                title={editingKey === null ? 'Agregar Nodo' : 'Editar Nodo'}
                placement="right"
                width={400}
                onClose={handleCloseDrawer}
                open={drawerVisible}
            >

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    {/* Campo de solo lectura para ver la Key del nodo */}
                    <Form.Item label="Node Key" name="nodeKey">
                        <Input readOnly />
                    </Form.Item>

                    {/* Parent Key */}
                    <Form.Item label="Parent Key" name="parentKey">
                        <Input placeholder="Key del nodo padre (opcional)" />
                    </Form.Item>

                    {/* Checkbox => isFinal (true/false) */}
                    <Form.Item label="¿Es nodo final?" name="isFinal" valuePropName="checked">
                        <Checkbox onChange={handleCheckboxChange}>Sí</Checkbox>
                    </Form.Item>

                    {/* Título */}
                    <Form.Item label="Título" name="title">
                        <Input placeholder="Nombre del nodo" />
                    </Form.Item>

                    {/* Campos extra => Condicionalmente visibles */}
                    {isFinal && (
                        <>
                            <Form.Item label="Tipo Actividad" name="tipoActividad">
                                <Input placeholder="Ej: Entrega activo" />
                            </Form.Item>

                            <Form.Item label="Actor" name="actor">
                                <Input placeholder="Ej: Participantes" />
                            </Form.Item>

                            <Form.Item label="MDV" name="mdv">
                                <Input placeholder="Actas, fotos, etc." />
                            </Form.Item>

                            <Form.Item label="# Actividades" name="noDeActividades">
                                <Input type="number" min={0} />
                            </Form.Item>

                            <Form.Item label="# Ejecutadas" name="noDeEjecutados">
                                <Input type="number" min={0} />
                            </Form.Item>
                        </>
                    )}

                    {/* 🔘 Botones de acción */}
                    <div style={{ marginTop: 24, display: "flex", justifyContent: "space-between" }}>
                        <Button type="default" onClick={handleCloseDrawer}>
                            Cancelar
                        </Button>

                        {/* Solo mostrar el botón eliminar si estamos editando */}
                        {editingKey !== null && (
                            <Button type="primary" danger onClick={handleDeleteNode}>
                                Eliminar Nodo
                            </Button>
                        )}

                        <Button type="primary" htmlType="submit">
                            {editingKey === null ? 'Agregar' : 'Guardar'}
                        </Button>
                    </div>





                </Form>


            </Drawer>

            <Button style={{ marginTop: 16 }} onClick={toggleShowJson}>
                {showJson ? 'Ocultar' : 'Mostrar'} JSON
            </Button>

            {showJson && (
                <div style={{ marginTop: 16 }}>
                    <Button
                        style={{ marginBottom: 8 }}
                        onClick={() => {
                            const jsonText = JSON.stringify(treeData, null, 2);
                            navigator.clipboard.writeText(jsonText)
                                .then(() => {
                                    message.success('¡JSON copiado al portapapeles!');
                                })
                                .catch(() => {
                                    message.error('Error al copiar. Intenta de nuevo.');
                                });
                        }}
                    >
                        Copiar JSON
                    </Button>

                    <pre
                        style={{
                            background: '#f0f0f0',
                            padding: 8,
                            maxHeight: '200px',
                            overflow: 'auto'
                        }}
                    >
                        {JSON.stringify(treeData, null, 2)}
                    </pre>
                </div>
            )}

        </div>
    );
}

export default DynamicTree;


